import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router';

import { useAuthToken } from '@repo/hooks/useAuthToken';
import { Form, TextInputField } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';
import { Dialog } from '@repo/ui/Dialog';
import { registerInput, useZodForm } from '@repo/hooks/useZodForm';
import { getLoginRedirect } from '@repo/util/getLoginRedirect';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@repo/ui/InputOTP';

import { useAuthFlow, AuthFlowState } from '@repo/auth/hooks/useAuthFlow';
import { ChallengeSelector } from '@repo/auth/components/ChallengeSelector';
import { Checkbox } from '@repo/ui/Checkbox';

import {
  SESSION_EXPIRED_MESSAGE,
  clearSessionExpired,
  isSessionExpired,
} from '@/features/auth/sessionExpired';

import { z } from 'zod';

const passwordLoginEndpoint = import.meta.env.VITE_PASSWORD_LOGIN_ENDPOINT!;
const mfaBaseEndpoint = import.meta.env.VITE_MFA_LOGIN_ENDPOINT;

if (!passwordLoginEndpoint) {
  throw new Error('VITE_PASSWORD_LOGIN_ENDPOINT not specified.');
}

export const loginSchema = z.object({
  email: z.string().email({ message: 'Please submit a valid email address.' }),
  password: z.string().min(1, { message: 'Please enter password' }),
});

export type LoginParams = z.infer<typeof loginSchema>;

function getChallengeConfig(type: string): {
  label: string;
  codeLength: number;
} {
  const upper = type.toUpperCase();
  switch (upper) {
    case 'TOTP':
    case 'AUTHENTICATOR_TOTP':
      return { label: 'Authenticator Code', codeLength: 6 };
    case 'EMAIL_TOTP':
      return { label: 'Email Code', codeLength: 8 };
    case 'SMS_TOTP':
      return { label: 'SMS Code', codeLength: 6 };
    case 'BACKUP_TOKEN':
      return { label: 'Backup Code', codeLength: 8 };
    default:
      return { label: 'Verification Code', codeLength: 6 };
  }
}

function requiresStart(type: string): boolean {
  const upper = type.toUpperCase();
  return upper === 'EMAIL_TOTP' || upper === 'SMS_TOTP';
}

function LoginForm() {
  const [, setToken] = useAuthToken();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  let redirectTo = searchParams.get('redirect_to');
  if (redirectTo === '/') {
    redirectTo = '';
  }

  const [code, setCode] = useState('');
  const [resending, setResending] = useState(false);
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  // Read once on mount; cleared from storage so a later visit doesn't repeat it.
  const [sessionExpired] = useState(isSessionExpired);
  useEffect(() => clearSessionExpired(), []);

  const {
    state,
    login,
    selectChallengeType,
    startChallenge,
    verifyChallenge,
    goBackToSelection,
    reset,
    trustDevice,
    setTrustDevice,
  } = useAuthFlow({
    passwordLoginEndpoint,
    challengeStartEndpoint: mfaBaseEndpoint
      ? `${mfaBaseEndpoint}/login/challenge/start`
      : undefined,
    challengeVerifyEndpoint: mfaBaseEndpoint
      ? `${mfaBaseEndpoint}/login/challenge/verify`
      : undefined,
    onSuccess: (session) => {
      setToken({
        id: session.id,
        expires_at: session.expires_at,
        token: session.token,
        user_id: session.user_id,
      });
    },
  });

  const form = useZodForm({
    schema: loginSchema,
    async onSubmit(params: LoginParams) {
      await login(params.email, params.password);
    },
  });

  useEffect(() => {
    if (state.status === 'error') {
      form.setError('root.server', {
        type: 'auth-error',
        message: state.message,
      });
    } else if (state.status === 'idle' || state.status === 'loading') {
      form.clearErrors('root.server');
    }
  }, [state.status, state.status === 'error' ? state.message : null, form]);

  useEffect(() => {
    if (state.status === 'success') {
      navigate(getLoginRedirect(redirectTo));
    }
  }, [state.status, navigate, redirectTo]);

  useEffect(() => {
    if (state.status === 'challenge') {
      setCode('');
    }
  }, [state.status, (state as Extract<AuthFlowState, { status: 'challenge' }>).selectedType]);

  const handleSelectType = useCallback(
    async (type: string) => {
      if (requiresStart(type)) {
        await startChallenge(type);
      } else {
        selectChallengeType(type);
      }
    },
    [selectChallengeType, startChallenge]
  );

  const handleResend = useCallback(async () => {
    if (state.status !== 'challenge' || !state.selectedType) return;
    setResending(true);
    try {
      await startChallenge(state.selectedType);
    } finally {
      setResending(false);
    }
  }, [state, startChallenge]);

  const handleVerifySubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!code) return;
      await verifyChallenge(code);
    },
    [code, verifyChallenge]
  );

  const isLoading = state.status === 'loading';
  const isChallengeOpen = state.status === 'challenge';
  const selectedType = state.status === 'challenge' ? state.selectedType : undefined;
  const challengeData = state.status === 'challenge' ? state.data : null;
  const challengeError = state.status === 'challenge' ? state.error : undefined;
  const config = selectedType ? getChallengeConfig(selectedType) : null;
  const showResend = selectedType ? requiresStart(selectedType) : false;

  const dialogTitle = selectedType
    ? `Enter your ${config?.label.toLowerCase()}`
    : 'Two-factor authentication required';

  return (
    <>
      <div className="flex flex-col items-center w-full mb-5 sm:mb-8">
        <h1 className="mb-2.5 sm:mb-5 text-4xl font-bold sm:text-5xl dark:text-blue-100">
          Welcome Back
        </h1>
        <p className="mb-5 dark:text-blue-100">
          Need an account?{' '}
          <button type="button" onClick={() => setIsSignUpOpen(true)} className="text-blue-700 underline dark:text-blue-400">
            Sign up here
          </button>
          .
        </p>
      </div>

      <div className="flex flex-col items-center w-full px-5 py-8 border xs:px-10 sm:px-16 bg-grey-200 border-grey-400 dark:bg-blue-1000 dark:border-grey-700 rounded-xl">
        <Form className="flex flex-col items-center w-full gap-6" {...form}>
          {!isChallengeOpen && <Form.Submitting>Logging in...</Form.Submitting>}
          <Form.Error autoClose={false} />
          {sessionExpired && state.status === 'idle' && (
            <div
              role="status"
              className="w-full p-3 text-sm text-blue-900 bg-blue-100 rounded-lg dark:bg-blue-900/30 dark:text-blue-100"
            >
              {SESSION_EXPIRED_MESSAGE}
            </div>
          )}

          <TextInputField label="Email" type="email" {...registerInput(form, 'email')} />

          <TextInputField type="password" label="Password" {...registerInput(form, 'password')} />

          <div className="flex flex-col items-center mt-4 sm:col-span-2">
            <Button size="md" type="submit" loading={isLoading}>
              Login
            </Button>
            <Link to="/forgot-password">
              <small className="dark:text-blue-300 text-grey-600">Forgot your password?</small>
            </Link>
          </div>
        </Form>
      </div>

      <Dialog isOpen={isChallengeOpen} setIsOpen={() => reset()} title={dialogTitle} centered>
        <div className="flex flex-col items-center w-full max-w-sm mx-auto gap-4">
          {challengeError && (
            <div className="w-full p-3 text-sm text-red-800 bg-red-100 dark:bg-red-900/30 dark:text-red-300 rounded-lg">
              {challengeError.message}
            </div>
          )}

          {!selectedType && challengeData ? (
            <ChallengeSelector
              challenges={challengeData.challenges}
              onSelect={handleSelectType}
              loading={isLoading}
            />
          ) : (
            <form onSubmit={handleVerifySubmit} className="flex flex-col items-center gap-6 w-full">
              <p className="text-center text-grey-600 dark:text-grey-300">
                {selectedType?.toUpperCase() === 'TOTP' ||
                selectedType?.toUpperCase() === 'AUTHENTICATOR_TOTP'
                  ? 'Enter the 6-digit code from your authenticator app'
                  : selectedType?.toUpperCase() === 'EMAIL_TOTP'
                    ? 'Enter the 8-digit code sent to your email'
                    : selectedType?.toUpperCase() === 'SMS_TOTP'
                      ? 'Enter the 6-digit code sent to your phone'
                      : selectedType?.toUpperCase() === 'BACKUP_TOKEN'
                        ? 'Enter one of your backup codes'
                        : 'Enter your verification code'}
              </p>

              <InputOTP
                value={code}
                onChange={setCode}
                maxLength={config?.codeLength || 6}
                disabled={isLoading}
              >
                <InputOTPGroup>
                  {Array.from({ length: config?.codeLength || 6 }).map((_, i) => (
                    <InputOTPSlot key={i} index={i} />
                  ))}
                </InputOTPGroup>
              </InputOTP>

              <div className="flex flex-col gap-3 w-full">
                <Button
                  type="submit"
                  disabled={code.length !== (config?.codeLength || 6) || isLoading}
                  loading={isLoading}
                >
                  Verify
                </Button>

                {showResend && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleResend}
                    disabled={resending || isLoading}
                    loading={resending}
                  >
                    Resend Code
                  </Button>
                )}

                <Button
                  type="button"
                  variant="tertiary"
                  onClick={goBackToSelection}
                  disabled={isLoading}
                >
                  Use a different method
                </Button>
              </div>
            </form>
          )}

          {(!selectedType || selectedType?.toUpperCase() !== 'BACKUP_TOKEN') && (
            <>
              <hr className="w-full border-grey-300 dark:border-grey-700" />
              <div className="flex justify-center w-full">
                <Checkbox
                  label="Trust this device for 30 days"
                  checked={trustDevice}
                  onClick={() => setTrustDevice(!trustDevice)}
                  additionalContainerClasses="!w-auto"
                />
              </div>
            </>
          )}
        </div>
      </Dialog>

      <Dialog isOpen={isSignUpOpen} setIsOpen={setIsSignUpOpen} title="Get Started" centered>
        <p className="text-center text-grey-600 dark:text-grey-300">
          To provide you with the best experience, we personally set up every new account.
          Please reach out to our team at{' '}
          <a href="mailto:sales@swiftsmsgateway.com" className="text-blue-700 underline dark:text-blue-400">
            sales@swiftsmsgateway.com
          </a>{' '}
          or call/text{' '}
          <a href="tel:1-800-264-7141" className="text-blue-700 underline dark:text-blue-400">
            1-800-264-7141
          </a>{' '}
          and we'll get you started right away.
        </p>
      </Dialog>
    </>
  );
}

export default LoginForm;
